import {
    canonicalDateTimeFormat,
    useUserDateTimeFormat,
} from 'custom_hooks/useUserDateTimeFormat';
import { format } from 'date-fns';
import styled from 'styled-components';

const Wrapper = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

const NowrapWrapper = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 0.5rem;
    white-space: nowrap;
`;

const TruncatedWrapper = styled.div`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.2;

    time {
        display: inline;
    }

    /* Add space after the first time element (date,) */
    time:first-child::after {
        content: ' ';
    }
`;

export type DateTimeDisplayProps = Readonly<{
    /**
     * The date time to display
     */
    date: Date | null | undefined;
    /**
     * Whether or not to show seconds in the time. Defaults to `true`
     */
    showSeconds?: boolean;
    /**
     * Whether to truncate the display to 2 lines with ellipsis
     */
    truncate?: boolean;
    /**
     * Whether to force the date and time onto a single line, never wrapping
     */
    nowrap?: boolean;
    /**
     * Optional classname
     */
    className?: string;

    fallback?: JSX.Element;
}>;

export function DateTimeDisplay({
    date,
    showSeconds = true,
    truncate = false,
    nowrap = false,
    className,
    fallback = <></>,
}: DateTimeDisplayProps) {
    const { formatDate, formatTime } = useUserDateTimeFormat(showSeconds);

    if (!date) {
        return fallback;
    }

    const canonicalDate = format(date, canonicalDateTimeFormat);
    const WrapperComponent = truncate
        ? TruncatedWrapper
        : nowrap
          ? NowrapWrapper
          : Wrapper;

    return (
        <WrapperComponent className={className}>
            <time dateTime={canonicalDate} title={canonicalDate}>
                {formatDate(date)},
            </time>
            <time dateTime={canonicalDate} title={canonicalDate}>
                {formatTime(date)}
            </time>
        </WrapperComponent>
    );
}
